import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Users, FlaskConical, ScanLine, TriangleAlert, ArrowRight } from "lucide-react";

const STAT_CARDS = [
  { key: "patients", label: "Pacientes", icon: Users, color: "text-primary" },
  { key: "lab_exams", label: "Exames laboratoriais", icon: FlaskConical, color: "text-chart-3" },
  { key: "imaging", label: "Exames de imagem", icon: ScanLine, color: "text-ai" },
  { key: "alerts", label: "Valores alterados", icon: TriangleAlert, color: "text-destructive" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/patients").then((r) => setPatients(r.data));
  }, []);

  return (
    <div className="p-6 sm:p-10 max-w-7xl">
      <div className="mb-8 animate-fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-ai mb-2">Painel clínico</p>
        <h1 className="font-heading text-4xl font-light tracking-tighter">
          Olá, {(user?.name || "")
            .split(" ")
            .filter((w) => !/^(dr\.?|dra\.?)$/i.test(w))[0] || user?.name}
          .
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl">
          Acompanhe exames, imagens e sinais vitais dos seus pacientes. A IA cruza os dados e revela
          tendências de melhora.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {STAT_CARDS.map((c, i) => {
          const Icon = c.icon;
          return (
            <Card key={c.key} data-testid={`stat-${c.key}`}
              className="p-6 border-border shadow-none hover:-translate-y-[2px] transition-transform duration-200 animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}>
              <Icon className={`w-5 h-5 ${c.color} mb-4`} />
              <div className="font-mono-data text-3xl font-semibold">{stats ? stats[c.key] : "—"}</div>
              <div className="text-sm text-muted-foreground mt-1">{c.label}</div>
            </Card>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl font-medium tracking-tight">Pacientes recentes</h2>
        <Link to="/pacientes" className="text-sm text-primary font-medium hover:underline flex items-center gap-1" data-testid="see-all-patients">
          Ver todos <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {patients.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">
          Nenhum paciente cadastrado ainda.{" "}
          <Link to="/pacientes" className="text-primary underline">Cadastre o primeiro</Link>.
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {patients.slice(0, 6).map((p) => (
            <Link key={p.id} to={`/pacientes/${p.id}`} data-testid={`patient-card-${p.id}`}>
              <Card className="p-5 border-border shadow-none hover:-translate-y-[2px] hover:border-primary/40 transition-transform duration-200">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading font-bold">
                    {p.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.sex || "—"} · {p.age ? `${p.age} anos` : "idade n/d"}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 mt-4 text-xs text-muted-foreground">
                  <span>{p.lab_count} lab</span>
                  <span>{p.imaging_count} imagens</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
